import React, { useEffect, useState } from 'react';
import { MONEY_DECIMALS } from '../../../../../utils/money';
import { Modal, MultiSelect, NumberInput, SegmentedControl, Select, Checkbox } from '@mantine/core';
import { Button } from '../../../../../components/Button';
import navyModal from '../../../../../components/Modal/navy-modal.module.scss';
import shared from '../shared/settings-shared.module.scss';
import classes from '../shared/product-settings.module.scss';

export type BulkProductEditFields = {
  categoryId?: number | null;
  price?: number;
  printerIds?: number[];
  kds?: boolean;
};

type SelectOption = { value: string; label: string };

type KdsChoice = 'unchanged' | 'on' | 'off';

const UNCHANGED_CATEGORY = null;
const CLEAR_CATEGORY_VALUE = '__none__';

type BulkEditProductsModalProps = {
  opened: boolean;
  onClose: () => void;
  selectedCount: number;
  categoryOptions: SelectOption[];
  printerOptions: SelectOption[];
  isSubmitting: boolean;
  onSubmit: (fields: BulkProductEditFields) => void;
};

// Architectural summary: Applies only the touched fields to every selected product.
const BulkEditProductsModalComponent: React.FC<BulkEditProductsModalProps> = ({
  opened,
  onClose,
  selectedCount,
  categoryOptions,
  printerOptions,
  isSubmitting,
  onSubmit,
}) => {
  const [categoryValue, setCategoryValue] = useState<string | null>(UNCHANGED_CATEGORY);
  const [priceValue, setPriceValue] = useState<number | ''>('');
  const [printersEnabled, setPrintersEnabled] = useState(false);
  const [printerValues, setPrinterValues] = useState<string[]>([]);
  const [kdsChoice, setKdsChoice] = useState<KdsChoice>('unchanged');

  useEffect(() => {
    if (opened) {
      setCategoryValue(UNCHANGED_CATEGORY);
      setPriceValue('');
      setPrintersEnabled(false);
      setPrinterValues([]);
      setKdsChoice('unchanged');
    }
  }, [opened]);

  const buildFields = (): BulkProductEditFields => {
    const fields: BulkProductEditFields = {};
    if (categoryValue !== null) {
      fields.categoryId = categoryValue === CLEAR_CATEGORY_VALUE ? null : Number(categoryValue);
    }
    if (priceValue !== '') {
      fields.price = Number(priceValue);
    }
    if (printersEnabled) {
      fields.printerIds = printerValues.map((value) => Number(value));
    }
    if (kdsChoice !== 'unchanged') {
      fields.kds = kdsChoice === 'on';
    }
    return fields;
  };

  const fields = buildFields();
  const hasChanges = Object.keys(fields).length > 0;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      onContextMenu={(event) => event.preventDefault()}
      title={`עריכה מרובה — ${selectedCount} מוצרים`}
      size="lg"
      centered
      radius="md"
      classNames={{ root: navyModal.root }}
      closeButtonProps={{ disabled: isSubmitting, 'data-testid': 'bulk-edit-close' } as React.ComponentProps<typeof Modal>['closeButtonProps']}
      closeOnClickOutside={!isSubmitting}
      closeOnEscape={!isSubmitting}
    >
      <section className={shared.section}>
        <p className={shared.sectionSubheading}>רק שדות שתמלא יוחלו על המוצרים הנבחרים.</p>

        <div className={classes.modalFields}>
          <Select
            data-testid="bulk-edit-category"
            label="קטגוריה"
            placeholder="ללא שינוי"
            data={[{ value: CLEAR_CATEGORY_VALUE, label: 'ללא קטגוריה' }, ...categoryOptions]}
            value={categoryValue}
            onChange={setCategoryValue}
            radius="md"
            clearable
            searchable
          />

          <NumberInput
            data-testid="bulk-edit-price"
            label="מחיר"
            placeholder="ללא שינוי"
            value={priceValue}
            onChange={(value) => setPriceValue(value === '' || value === null || value === undefined ? '' : Number(value))}
            min={0}
            max={9999999999.99}
            decimalScale={MONEY_DECIMALS}
            allowDecimal={MONEY_DECIMALS !== 0}
            radius="md"
          />

          <div>
            <Checkbox
              data-testid="bulk-edit-printers-toggle"
              label="עדכן שיוך מדפסות"
              checked={printersEnabled}
              onChange={(event) => setPrintersEnabled(event.currentTarget.checked)}
            />
            {printersEnabled && (
              <MultiSelect
                data-testid="bulk-edit-printers"
                mt="xs"
                placeholder="בחר מדפסות (רשימה ריקה מנקה שיוך)"
                data={printerOptions}
                value={printerValues}
                onChange={setPrinterValues}
                radius="md"
                searchable
                clearable
              />
            )}
          </div>

          <div>
            <div className={classes.fieldLabel}>KDS</div>
            <SegmentedControl
              data-testid="bulk-edit-kds"
              value={kdsChoice}
              onChange={(value) => setKdsChoice(value as KdsChoice)}
              data={[
                { value: 'unchanged', label: 'ללא שינוי' },
                { value: 'on', label: 'פעיל' },
                { value: 'off', label: 'כבוי' },
              ]}
            />
          </div>
        </div>

        <div className={shared.formActions}>
          <Button variant="cancel" onClick={onClose} disabled={isSubmitting} dataTestId="bulk-edit-cancel">
            ביטול
          </Button>
          <Button
            variant="save"
            onClick={() => onSubmit(fields)}
            disabled={!hasChanges || isSubmitting}
            loading={isSubmitting}
            loadingLabel="שומר..."
            dataTestId="bulk-edit-apply"
          >
            החל על הנבחרים
          </Button>
        </div>
      </section>
    </Modal>
  );
};

export const BulkEditProductsModal = React.memo(BulkEditProductsModalComponent);
